import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { 
  Ship, 
  Plane, 
  Truck, 
  Package, 
  Warehouse, 
  ArrowRight,
  CheckCircle,
  Globe,
  Clock,
  Shield,
  Anchor,
  Container,
  MapPin
} from "lucide-react";
import { Button } from "@/components/ui/button";

const fadeInUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6 } }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.1 }
  }
};

// Page Header
const PageHeader = () => {
  return (
    <section className="relative pt-32 pb-20 bg-[#0B2545]" data-testid="services-header">
      <div className="absolute inset-0 opacity-10">
        <div className="absolute inset-0 bg-[linear-gradient(to_right,#ffffff12_1px,transparent_1px),linear-gradient(to_bottom,#ffffff12_1px,transparent_1px)] bg-[size:40px_40px]" />
      </div>
      <div className="relative max-w-7xl mx-auto px-6 md:px-12">
        <motion.div
          initial="hidden"
          animate="visible"
          variants={staggerContainer}
        >
          <motion.p 
            variants={fadeInUp}
            className="uppercase tracking-[0.2em] text-xs font-bold text-[#F59E0B] mb-4"
          >
            Comprehensive Logistics Solutions
          </motion.p>
          <motion.h1 
            variants={fadeInUp}
            className="heading-font text-4xl sm:text-5xl lg:text-6xl font-bold text-white uppercase tracking-tight leading-none mb-6"
          >
            Our Services
          </motion.h1>
          <motion.p 
            variants={fadeInUp}
            className="text-lg text-slate-300 max-w-2xl"
          >
            From ocean shipping to air freight, ground transportation to warehousing, 
            we offer end-to-end logistics solutions designed to move your business forward.
          </motion.p>
        </motion.div>
      </div>
    </section>
  );
};

// Sea Freight Section
const SeaFreightSection = () => {
  const features = [
    "Full Container Load (FCL) shipping",
    "Less than Container Load (LCL) options",
    "Refrigerated container services",
    "Oversized and project cargo handling",
    "Port-to-port and door-to-door delivery",
    "Customs clearance assistance"
  ];

  return (
    <section className="py-24 bg-white" data-testid="sea-freight-section">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
          variants={staggerContainer}
          className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center"
        >
          <motion.div variants={fadeInUp} className="order-2 lg:order-1">
            <div className="flex items-center gap-4 mb-6">
              <div className="w-14 h-14 bg-[#0B2545] flex items-center justify-center">
                <Ship className="w-7 h-7 text-[#F59E0B]" />
              </div>
              <h2 className="heading-font text-3xl sm:text-4xl font-bold text-[#0B2545] uppercase tracking-tight">
                Sea Freight
              </h2>
            </div>
            
            <p className="text-slate-600 leading-relaxed mb-6">
              Our sea freight services provide cost-effective solutions for shipping large volumes of cargo 
              across the world's oceans. With partnerships at major ports globally, we ensure your goods 
              move seamlessly from origin to destination.
            </p>
            
            <p className="text-slate-600 leading-relaxed mb-8">
              Whether you're shipping raw materials, finished products, or specialized equipment, our 
              experienced team handles every aspect of your ocean freight needs. We offer flexible 
              scheduling options and competitive rates to match your business requirements.
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-8">
              {features.map((feature, index) => (
                <div key={index} className="flex items-center gap-2">
                  <CheckCircle className="w-5 h-5 text-[#F59E0B] flex-shrink-0" />
                  <span className="text-sm text-slate-700">{feature}</span>
                </div>
              ))}
            </div>

            <div className="flex items-center gap-6 p-6 bg-slate-50 border-l-4 border-[#F59E0B]">
              <Anchor className="w-10 h-10 text-[#0B2545]" />
              <div>
                <p className="font-bold text-[#0B2545]">Major Port Coverage</p>
                <p className="text-sm text-slate-600">Access to 200+ ports across 6 continents</p>
              </div>
            </div>
          </motion.div>

          <motion.div variants={fadeInUp} className="order-1 lg:order-2">
            <div className="relative">
              <img 
                src="https://images.unsplash.com/photo-1760385280608-935bfeaab4ac?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NzV8MHwxfHNlYXJjaHwzfHxjYXJnbyUyMHNoaXAlMjBjb250YWluZXIlMjBzZWElMjBmcmVpZ2h0fGVufDB8fHx8MTc3MzUzODM5MHww&ixlib=rb-4.1.0&q=85"
                alt="Sea Freight"
                className="w-full h-[400px] object-cover"
              />
              <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-[#0B2545] to-transparent p-6">
                <p className="text-white font-bold">Reliable Ocean Shipping</p>
                <p className="text-slate-300 text-sm">Transit times from 2-6 weeks depending on route</p>
              </div>
            </div>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

// Air Freight Section
const AirFreightSection = () => {
  const features = [
    "Express and standard air cargo options",
    "Temperature-controlled shipments",
    "Dangerous goods handling (IATA certified)",
    "Charter services for urgent cargo",
    "Real-time flight tracking",
    "Airport-to-airport and door-to-door"
  ];

  return (
    <section className="py-24 bg-slate-50" data-testid="air-freight-section">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
          variants={staggerContainer}
          className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center"
        >
          <motion.div variants={fadeInUp}>
            <div className="relative">
              <img 
                src="https://images.unsplash.com/photo-1689820583232-70cbc40e75da?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzF8MHwxfHNlYXJjaHw0fHxhaXIlMjBjYXJnbyUyMHBsYW5lJTIwZnJlaWdodCUyMGxvYWRpbmd8ZW58MHx8fHwxNzczNTM4MzkxfDA&ixlib=rb-4.1.0&q=85"
                alt="Air Freight"
                className="w-full h-[400px] object-cover"
              />
              <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-[#0B2545] to-transparent p-6">
                <p className="text-white font-bold">Fast Air Cargo Solutions</p>
                <p className="text-slate-300 text-sm">Delivery within 1-5 days worldwide</p>
              </div>
            </div>
          </motion.div>

          <motion.div variants={fadeInUp}>
            <div className="flex items-center gap-4 mb-6">
              <div className="w-14 h-14 bg-[#0B2545] flex items-center justify-center">
                <Plane className="w-7 h-7 text-[#F59E0B]" />
              </div>
              <h2 className="heading-font text-3xl sm:text-4xl font-bold text-[#0B2545] uppercase tracking-tight">
                Air Freight
              </h2>
            </div>
            
            <p className="text-slate-600 leading-relaxed mb-6">
              When time is of the essence, our air freight services deliver unmatched speed and reliability. 
              We partner with leading airlines to provide comprehensive air cargo solutions that keep your 
              supply chain moving at the pace of your business.
            </p>
            
            <p className="text-slate-600 leading-relaxed mb-8">
              From urgent documents to high-value electronics, perishable goods to oversized machinery, 
              our air freight experts design customized solutions that balance speed, cost, and security 
              for every shipment.
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-8">
              {features.map((feature, index) => (
                <div key={index} className="flex items-center gap-2">
                  <CheckCircle className="w-5 h-5 text-[#F59E0B] flex-shrink-0" />
                  <span className="text-sm text-slate-700">{feature}</span>
                </div>
              ))}
            </div>

            <div className="flex items-center gap-6 p-6 bg-white border-l-4 border-[#F59E0B]">
              <Clock className="w-10 h-10 text-[#0B2545]" />
              <div>
                <p className="font-bold text-[#0B2545]">Express Delivery</p>
                <p className="text-sm text-slate-600">Next-day delivery available for urgent shipments</p>
              </div>
            </div>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

// Land Freight Section
const LandFreightSection = () => {
  const features = [
    "Full Truckload (FTL) services",
    "Less than Truckload (LTL) options",
    "Cross-border transportation",
    "Specialized vehicle fleet",
    "GPS tracking on all vehicles",
    "Scheduled and express delivery"
  ];

  return (
    <section className="py-24 bg-white" data-testid="land-freight-section">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
          variants={staggerContainer}
          className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center"
        >
          <motion.div variants={fadeInUp} className="order-2 lg:order-1">
            <div className="flex items-center gap-4 mb-6">
              <div className="w-14 h-14 bg-[#0B2545] flex items-center justify-center">
                <Truck className="w-7 h-7 text-[#F59E0B]" />
              </div>
              <h2 className="heading-font text-3xl sm:text-4xl font-bold text-[#0B2545] uppercase tracking-tight">
                Land Freight
              </h2>
            </div>
            
            <p className="text-slate-600 leading-relaxed mb-6">
              Our land freight services provide flexible and reliable road transportation across Europe 
              and beyond. With a modern fleet and experienced drivers, we deliver your cargo safely 
              and on schedule, every time.
            </p>
            
            <p className="text-slate-600 leading-relaxed mb-8">
              From local distribution to long-haul international routes, our ground transportation 
              solutions are designed to meet the diverse needs of businesses of all sizes. We offer 
              both dedicated and shared trucking options to optimize cost and efficiency.
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-8">
              {features.map((feature, index) => (
                <div key={index} className="flex items-center gap-2">
                  <CheckCircle className="w-5 h-5 text-[#F59E0B] flex-shrink-0" />
                  <span className="text-sm text-slate-700">{feature}</span>
                </div>
              ))}
            </div>

            <div className="flex items-center gap-6 p-6 bg-slate-50 border-l-4 border-[#F59E0B]">
              <MapPin className="w-10 h-10 text-[#0B2545]" />
              <div>
                <p className="font-bold text-[#0B2545]">European Coverage</p>
                <p className="text-sm text-slate-600">Direct routes to all major European destinations</p>
              </div>
            </div>
          </motion.div>

          <motion.div variants={fadeInUp} className="order-1 lg:order-2">
            <div className="relative">
              <img 
                src="https://images.unsplash.com/photo-1766608422198-5be9ac0aac9e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzl8MHwxfHNlYXJjaHwxfHxzZW1pJTIwdHJ1Y2slMjBjYXJnbyUyMGxvZ2lzdGljcyUyMGhpZ2h3YXl8ZW58MHx8fHwxNzczNTM4MzkyfDA&ixlib=rb-4.1.0&q=85"
                alt="Land Freight"
                className="w-full h-[400px] object-cover"
              />
              <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-[#0B2545] to-transparent p-6">
                <p className="text-white font-bold">Road Transportation Excellence</p>
                <p className="text-slate-300 text-sm">Modern fleet with real-time tracking</p>
              </div>
            </div>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

// Additional Services Section
const AdditionalServicesSection = () => {
  const services = [
    {
      icon: Package,
      title: "Courier Service",
      description: "Fast and secure door-to-door courier services for parcels and documents. Perfect for time-sensitive deliveries requiring special handling and tracking.",
      features: ["Same-day delivery available", "Signature confirmation", "Insurance coverage", "Online tracking"]
    },
    {
      icon: Warehouse,
      title: "Warehousing & Distribution",
      description: "Modern storage facilities with advanced inventory management systems. We provide pick and pack services, order fulfillment, and distribution logistics.",
      features: ["Climate-controlled storage", "Inventory management", "Order fulfillment", "Cross-docking services"]
    },
    {
      icon: Container,
      title: "Direct Freight Services",
      description: "Dedicated full-load services for oversized, heavy, or specialized cargo. Our direct freight solutions ensure your shipment gets exclusive handling and priority routing.",
      features: ["Dedicated vehicles", "Project cargo handling", "Heavy lift capability", "Door-to-door service"]
    }
  ];

  return (
    <section className="py-24 bg-slate-50" data-testid="additional-services-section">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
          variants={staggerContainer}
        >
          <motion.div variants={fadeInUp} className="text-center mb-16">
            <p className="uppercase tracking-[0.2em] text-xs font-bold text-[#F59E0B] mb-4">More Solutions</p>
            <h2 className="heading-font text-3xl sm:text-4xl font-bold text-[#0B2545] uppercase tracking-tight mb-4">
              Additional Services
            </h2>
            <p className="text-slate-600 max-w-2xl mx-auto">
              Beyond traditional freight, we offer specialized services to meet all your logistics needs
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {services.map((service, index) => {
              const Icon = service.icon;
              return (
                <motion.div
                  key={index}
                  variants={fadeInUp}
                  className="bg-white border border-slate-200 p-8 hover:border-[#F59E0B] hover:shadow-xl transition-all duration-300"
                  data-testid={`additional-service-${index}`}
                >
                  <div className="w-14 h-14 bg-[#0B2545] flex items-center justify-center mb-6">
                    <Icon className="w-7 h-7 text-[#F59E0B]" />
                  </div>
                  <h3 className="heading-font text-xl font-bold text-[#0B2545] uppercase tracking-tight mb-4">
                    {service.title}
                  </h3>
                  <p className="text-slate-600 text-sm leading-relaxed mb-6">
                    {service.description}
                  </p>
                  <ul className="space-y-2">
                    {service.features.map((feature, i) => (
                      <li key={i} className="flex items-center gap-2 text-sm text-slate-700">
                        <CheckCircle className="w-4 h-4 text-[#F59E0B] flex-shrink-0" />
                        {feature}
                      </li>
                    ))}
                  </ul>
                </motion.div>
              );
            })}
          </div>
        </motion.div>
      </div>
    </section>
  );
};

// CTA Section
const CTASection = () => {
  return (
    <section className="py-20 bg-[#0B2545]" data-testid="services-cta">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={staggerContainer}
          className="text-center"
        >
          <motion.h2 
            variants={fadeInUp}
            className="heading-font text-3xl sm:text-4xl font-bold text-white uppercase tracking-tight mb-4"
          >
            Need a Custom Solution?
          </motion.h2>
          <motion.p 
            variants={fadeInUp}
            className="text-slate-300 mb-8 max-w-xl mx-auto"
          >
            Our logistics experts are ready to design a tailored shipping solution for your unique requirements.
          </motion.p>
          <motion.div variants={fadeInUp}>
            <Link to="/contact">
              <Button 
                className="bg-[#F59E0B] hover:bg-[#D97706] text-black font-bold uppercase tracking-wide h-12 px-8 rounded-sm transition-all duration-300"
                data-testid="services-cta-btn"
              >
                Request a Quote <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            </Link>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

const ServicesPage = () => {
  return (
    <div data-testid="services-page">
      <PageHeader />
      <SeaFreightSection />
      <AirFreightSection />
      <LandFreightSection />
      <AdditionalServicesSection />
      <CTASection />
    </div>
  );
};

export default ServicesPage;
